import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

// All available metrics
const ALL_METRICS = [
  { key: 'market_cap', label: 'Market Cap' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'cost_of_revenue', label: 'Cost of Revenue' },
  { key: 'net_income', label: 'Net Income' },
  { key: 'research_development', label: 'R&D Expense' },
  { key: 'diluted_eps', label: 'Diluted EPS' },
  { key: 'ebitda', label: 'EBITDA' },
  { key: 'price_to_earnings', label: 'P/E Ratio' },
  { key: 'avg_price', label: 'Avg Price' },
  { key: 'year_end_price', label: 'Year-End Price' },
  { key: 'median_price', label: 'Median Price' },
  { key: 'dividend_per_share', label: 'Dividend Per Share' },
  { key: 'gross_profit', label: 'Gross Profit' },
  { key: 'operating_income', label: 'Operating Income' },
  { key: 'gross_margin_pct', label: 'Gross Margin %' },
  { key: 'operating_margin_pct', label: 'Operating Margin %' },
  { key: 'net_margin_pct', label: 'Net Margin %' },
  { key: 'free_cash_flow', label: 'Free Cash Flow' },
  { key: 'shares_outstanding', label: 'Shares Outstanding' },
  { key: 'return_on_equity', label: 'Return on Equity' },
  { key: 'total_debt', label: 'Total Debt' },
  { key: 'peg_ratio', label: 'PEG Ratio' },
];

// Beginner-friendly tooltip descriptions for each metric
const METRIC_TOOLTIPS = {
  market_cap: 'Total value of the company — share price × shares outstanding. Shown per year-end (current price for TTM).',
  revenue: 'Total money earned from selling products or services, before any expenses are subtracted.',
  cost_of_revenue: 'The direct costs to produce what was sold — materials, labor, and manufacturing.',
  net_income: "The company's total profit after ALL expenses, taxes, and costs. Also called the \"bottom line.\"",
  research_development: 'Money spent on creating new products, technologies, or improving existing ones (R&D).',
  diluted_eps: 'Earnings Per Share — profit divided by total shares outstanding. Higher means more profit per share.',
  ebitda: 'Earnings before interest, taxes, depreciation & amortization. A measure of core operating profitability.',
  price_to_earnings: 'Stock price ÷ earnings per share. A lower P/E may suggest the stock is undervalued relative to its earnings.',
  avg_price: 'The average daily closing price of the stock over the full year.',
  year_end_price: "The stock's closing price on the last trading day of the year.",
  median_price: 'The middle stock price for the year — half the trading days were above this price, half below.',
  dividend_per_share: 'Total cash dividends paid per share during the year. Not all companies pay dividends.',
  gross_profit: 'Revenue minus cost of revenue. Shows how much money is left before operating expenses.',
  operating_income: 'Profit from core business operations — after operating costs, but before interest and taxes.',
  gross_margin_pct: 'Gross profit as a % of revenue. Higher means more profit kept from each dollar of sales.',
  operating_margin_pct: 'Operating income as a % of revenue. Shows how efficiently the company runs day-to-day.',
  net_margin_pct: 'Net income as a % of revenue. Shows how many cents of profit come from each dollar earned.',
  free_cash_flow: 'Cash from operations minus capital spending. The actual cash generated after maintaining assets.',
  shares_outstanding: 'Total number of shares held by all investors. Used to calculate per-share metrics like EPS.',
  return_on_equity: 'Net income ÷ shareholder equity. Measures how effectively the company uses investor money.',
  total_debt: 'Combined short-term and long-term debt. Shows how much the company owes to lenders.',
  peg_ratio: 'P/E ratio ÷ EPS growth rate. A PEG of 1 means fairly valued for its growth. Below 1 may be undervalued, above 1 may be overvalued.',
};

// Format functions for different metric types
const formatValue = (value, metricKey) => {
  if (value == null || isNaN(value)) return '--';

  // Large dollar amounts (revenue, cost of revenue, net income, R&D, EBITDA, gross profit, operating income, FCF, total debt)
  if (['market_cap', 'revenue', 'cost_of_revenue', 'net_income', 'research_development', 'ebitda',
       'gross_profit', 'operating_income', 'free_cash_flow', 'total_debt'].includes(metricKey)) {
    const num = Number(value);
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
    return `${sign}$${abs.toLocaleString()}`;
  }

  // Shares outstanding - large number without $ sign
  if (metricKey === 'shares_outstanding') {
    const num = Number(value);
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toLocaleString();
  }

  // EPS and Dividend Per Share - show as dollar with 2 decimals
  if (['diluted_eps', 'dividend_per_share'].includes(metricKey)) {
    return `$${Number(value).toFixed(2)}`;
  }

  // Stock prices (avg, year-end, median) - format as $XXX.XX
  if (['avg_price', 'year_end_price', 'median_price'].includes(metricKey)) {
    return `$${Number(value).toFixed(2)}`;
  }

  // Margin percentages and ROE - show as XX.X%
  if (['gross_margin_pct', 'operating_margin_pct', 'net_margin_pct', 'return_on_equity'].includes(metricKey)) {
    return `${Number(value).toFixed(1)}%`;
  }

  // Ratios (P/E) - just show number with 2 decimals
  return Number(value).toFixed(2);
};

// Helper function to calculate YoY growth
const calculateGrowth = (currentValue, previousValue) => {
  if (currentValue == null || previousValue == null || previousValue === 0) {
    return null;
  }
  return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
};

// Format growth percentage
const formatGrowth = (value) => {
  if (value == null) return '--';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Muted colors for ticker columns in comparison mode
const TICKER_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
];

function ResearchFinancialsTable({ data, years, onYearsChange, onTickerClick }) {
  // State: selected metrics (default all selected)
  const [selectedMetrics, setSelectedMetrics] = useState(
    ALL_METRICS.map(m => m.key)
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [showGrowthRows, setShowGrowthRows] = useState(false);
  const [viewMode, setViewMode] = useState('statement'); // 'default' or 'statement'
  const [visibleTickers, setVisibleTickers] = useState([]);
  const dropdownRef = useRef(null);

  // Sync visibleTickers when data.tickers changes
  useEffect(() => {
    if (data?.tickers) {
      setVisibleTickers(data.tickers);
    }
  }, [data?.tickers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle a metric selection
  const toggleMetric = (metricKey) => {
    setSelectedMetrics(prev =>
      prev.includes(metricKey)
        ? prev.filter(k => k !== metricKey)
        : [...prev, metricKey]
    );
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedMetrics.length === ALL_METRICS.length) {
      setSelectedMetrics([]);
    } else {
      setSelectedMetrics(ALL_METRICS.map(m => m.key));
    }
  };

  // Get ordered selected metrics (maintain original order)
  const orderedSelectedMetrics = useMemo(() => {
    return ALL_METRICS.filter(m => selectedMetrics.includes(m.key));
  }, [selectedMetrics]);

  // Filter years based on the years prop (5 or 10)
  const filteredYears = useMemo(() => {
    if (!data?.years) return [];

    // Get numeric years (exclude 'TTM')
    const numericYears = data.years.filter(y => typeof y === 'number');

    // Take only the last N years based on the years prop
    const lastNYears = numericYears.slice(-years);

    // Add TTM back if it exists
    if (data.years.includes('TTM')) {
      return [...lastNYears, 'TTM'];
    }

    return lastNYears;
  }, [data?.years, years]);

  // Export to CSV
  const exportToCSV = () => {
    if (!data || !filteredYears.length || orderedSelectedMetrics.length === 0) return;

    let csvRows;

    if (viewMode === 'statement') {
      // Statement view: rows=metrics, cols=years×tickers
      const tickers = visibleTickers.length > 0 ? visibleTickers : data.tickers;
      const isSingle = tickers.length === 1;

      // Build headers — tickers as top grouping, years underneath
      const headers = ['Metric'];
      if (isSingle) {
        filteredYears.forEach(year => headers.push(String(year)));
      } else {
        tickers.forEach(ticker => {
          filteredYears.forEach(year => headers.push(`${ticker}_${year}`));
        });
      }
      csvRows = [headers.join(',')];

      // Build metric rows
      orderedSelectedMetrics.forEach(metric => {
        const values = [metric.label];
        if (isSingle) {
          filteredYears.forEach(year => {
            const val = data.ratios?.[metric.key]?.[tickers[0]]?.[year];
            values.push(val != null ? val : '');
          });
        } else {
          tickers.forEach(ticker => {
            filteredYears.forEach(year => {
              const val = data.ratios?.[metric.key]?.[ticker]?.[year];
              values.push(val != null ? val : '');
            });
          });
        }
        csvRows.push(values.join(','));
      });
    } else {
      // Default view: rows=tickers, cols=metrics×years
      const headers = ['Ticker'];
      orderedSelectedMetrics.forEach(metric => {
        filteredYears.forEach(year => {
          headers.push(`${metric.label}_${year}`);
        });
      });
      csvRows = [headers.join(',')];

      data.tickers.forEach(ticker => {
        const values = [ticker];
        orderedSelectedMetrics.forEach(metric => {
          filteredYears.forEach(year => {
            const val = data.ratios?.[metric.key]?.[ticker]?.[year];
            values.push(val != null ? val : '');
          });
        });
        csvRows.push(values.join(','));
      });
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const viewSuffix = viewMode === 'statement' ? '_statement' : '';
    a.download = `financials_${years}yr${viewSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!data || !data.tickers || data.tickers.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No financials data available. Run an analysis from the Analysis Input tab.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls Row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Metrics Multi-Select Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
          >
            <span>Select Metrics ({selectedMetrics.length}/{ALL_METRICS.length})</span>
            <svg className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDropdown && (
            <div className="absolute z-20 mt-1 w-56 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-80 overflow-y-auto">
              {/* Select All */}
              <label className="flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer border-b border-gray-700">
                <input
                  type="checkbox"
                  checked={selectedMetrics.length === ALL_METRICS.length}
                  onChange={toggleAll}
                  className="mr-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-white font-medium">Select All</span>
              </label>

              {/* Individual metrics */}
              {ALL_METRICS.map(metric => (
                <label
                  key={metric.key}
                  className="flex items-center px-4 py-2 hover:bg-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMetrics.includes(metric.key)}
                    onChange={() => toggleMetric(metric.key)}
                    className="mr-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-white">{metric.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Years Toggle */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">History:</label>
          <div className="flex rounded overflow-hidden border border-gray-600">
            <button
              onClick={() => onYearsChange(5)}
              className={`px-3 py-1.5 text-sm ${years === 5 ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              5 Years
            </button>
            <button
              onClick={() => onYearsChange(10)}
              className={`px-3 py-1.5 text-sm ${years === 10 ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              10 Years
            </button>
          </div>
        </div>

        {/* Growth Rows Toggle */}
        <button
          onClick={() => setShowGrowthRows(!showGrowthRows)}
          className={`px-4 py-2 text-sm border rounded flex items-center gap-2 transition-colors ${
            showGrowthRows
              ? 'bg-green-600 border-green-500 text-white'
              : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          {showGrowthRows ? 'Hide Growth' : 'Show Growth'}
        </button>

        {/* View Toggle */}
        <div className="flex bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('statement')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'statement'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Statement View
          </button>
          <button
            onClick={() => setViewMode('default')}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              viewMode === 'default'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Horizontal View
          </button>
        </div>

        {/* Export Button */}
        <button
          onClick={exportToCSV}
          disabled={orderedSelectedMetrics.length === 0}
          className="px-4 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Selected metrics display */}
      {orderedSelectedMetrics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {orderedSelectedMetrics.map(metric => (
            <span
              key={metric.key}
              className="px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full flex items-center gap-1"
            >
              {metric.label}
              <button
                onClick={() => toggleMetric(metric.key)}
                className="ml-1 hover:text-blue-100"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Ticker comparison pills (Statement View only, multiple tickers) */}
      {viewMode === 'statement' && data?.tickers?.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-400 mr-1">Comparing:</span>
          {data.tickers.map((ticker, idx) => {
            const isVisible = visibleTickers.includes(ticker);
            const color = TICKER_COLORS[idx % TICKER_COLORS.length];
            return (
              <button
                key={ticker}
                onClick={() => {
                  if (isVisible) {
                    // Don't allow removing the last ticker
                    if (visibleTickers.length > 1) {
                      setVisibleTickers(prev => prev.filter(t => t !== ticker));
                    }
                  } else {
                    setVisibleTickers(prev => [...prev, ticker]);
                  }
                }}
                className={`px-3 py-1 text-xs rounded-full flex items-center gap-1 transition-colors border ${
                  isVisible
                    ? 'text-white border-current'
                    : 'text-gray-500 border-gray-600 bg-gray-800/50 hover:text-gray-300'
                }`}
                style={isVisible ? { borderColor: color, backgroundColor: `${color}20`, color } : {}}
              >
                {ticker}
                {isVisible && visibleTickers.length > 1 && (
                  <span className="ml-1 hover:opacity-70">×</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* No metrics selected warning */}
      {orderedSelectedMetrics.length === 0 && (
        <div className="text-center py-4 text-yellow-400 bg-yellow-900/20 rounded">
          Please select at least one metric to display.
        </div>
      )}

      {/* Default Table View */}
      {orderedSelectedMetrics.length > 0 && viewMode === 'default' && (
        <div className="overflow-x-auto border border-gray-700 rounded-lg">
          <table className="w-full text-sm min-w-max">
            <thead>
              {/* Row 1: Metric names spanning years */}
              <tr className="border-b border-gray-700" style={{ backgroundColor: '#1f2937' }}>
                <th
                  rowSpan={2}
                  className="text-left py-3 px-4 sticky left-0 z-10 font-semibold"
                  style={{ backgroundColor: '#1f2937', color: '#ffffff', minWidth: '80px' }}
                >
                  Ticker
                </th>
                {orderedSelectedMetrics.map(metric => (
                  <th
                    key={metric.key}
                    colSpan={filteredYears.length}
                    className="text-center py-2 px-2 font-semibold border-l border-gray-600 cursor-help"
                    style={{ backgroundColor: '#1f2937', color: '#60a5fa' }}
                    data-tooltip-id="financials-metric-tooltip"
                    data-tooltip-content={METRIC_TOOLTIPS[metric.key]}
                  >
                    {metric.label}
                  </th>
                ))}
              </tr>

              {/* Row 2: Years repeated for each metric */}
              <tr className="border-b border-gray-700" style={{ backgroundColor: '#111827' }}>
                {orderedSelectedMetrics.map((metric, metricIdx) =>
                  filteredYears.map((year, yearIdx) => (
                    <th
                      key={`${metric.key}-${year}`}
                      className={`text-right py-2 px-3 text-xs font-normal ${yearIdx === 0 ? 'border-l border-gray-600' : ''}`}
                      style={{ backgroundColor: '#111827', color: '#9ca3af', minWidth: '75px' }}
                    >
                      {year}
                    </th>
                  ))
                )}
              </tr>
            </thead>

            <tbody>
              {data.tickers.map((ticker, rowIdx) => (
                <React.Fragment key={ticker}>
                  {/* Main data row */}
                  <tr
                    className={`border-b border-gray-800 hover:bg-[rgba(33,138,255,0.15)] transition-colors ${rowIdx % 2 === 0 ? 'bg-gray-800/30' : ''}`}
                  >
                    <td
                      className="py-2 px-4 font-medium text-white sticky left-0"
                      style={{ backgroundColor: rowIdx % 2 === 0 ? '#1f2937' : '#111827' }}
                    >
                      <span
                        className="cursor-pointer text-blue-400 hover:text-blue-300 hover:underline"
                        onClick={() => onTickerClick && onTickerClick(ticker)}
                      >
                        {ticker}
                      </span>
                    </td>
                    {orderedSelectedMetrics.map((metric, metricIdx) =>
                      filteredYears.map((year, yearIdx) => (
                        <td
                          key={`${ticker}-${metric.key}-${year}`}
                          className={`py-2 px-3 text-right text-white ${yearIdx === 0 ? 'border-l border-gray-700' : ''}`}
                        >
                          {formatValue(data.ratios?.[metric.key]?.[ticker]?.[year], metric.key)}
                        </td>
                      ))
                    )}
                  </tr>

                  {/* Growth row - only shown when toggled */}
                  {showGrowthRows && (
                    <tr
                      className={`border-b border-gray-700 ${rowIdx % 2 === 0 ? 'bg-gray-800/20' : 'bg-gray-900/30'}`}
                    >
                      <td
                        className="py-1 px-4 text-xs text-gray-400 italic sticky left-0"
                        style={{ backgroundColor: rowIdx % 2 === 0 ? '#1a2332' : '#0f1419' }}
                      >
                        {ticker} Growth
                      </td>
                      {orderedSelectedMetrics.map((metric, metricIdx) =>
                        filteredYears.map((year, yearIdx) => {
                          const currentVal = data.ratios?.[metric.key]?.[ticker]?.[year];
                          const prevYear = yearIdx > 0 ? filteredYears[yearIdx - 1] : null;
                          const prevVal = prevYear ? data.ratios?.[metric.key]?.[ticker]?.[prevYear] : null;
                          const growth = yearIdx === 0 ? null : calculateGrowth(currentVal, prevVal);

                          return (
                            <td
                              key={`${ticker}-${metric.key}-${year}-growth`}
                              className={`py-1 px-3 text-right text-xs ${yearIdx === 0 ? 'border-l border-gray-700' : ''} ${
                                growth === null ? 'text-gray-500' :
                                growth >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {formatGrowth(growth)}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <ReactTooltip
            id="financials-metric-tooltip"
            place="bottom"
            delayShow={300}
            delayHide={100}
            style={{ maxWidth: '300px', zIndex: 50 }}
          />
        </div>
      )}

      {/* Statement View Table (transposed: metrics as rows, years×tickers as columns) */}
      {orderedSelectedMetrics.length > 0 && viewMode === 'statement' && (
        <div className="overflow-x-auto border border-gray-700 rounded-lg">
          <table className="w-full text-sm min-w-max">
            <thead>
              {/* Single ticker: just years as columns */}
              {visibleTickers.length === 1 ? (
                <tr className="border-b border-gray-700" style={{ backgroundColor: '#1f2937' }}>
                  <th
                    className="text-left py-3 px-4 sticky left-0 z-10 font-semibold"
                    style={{ backgroundColor: '#1f2937', color: '#ffffff', minWidth: '160px' }}
                  >
                    <span
                      className="cursor-pointer text-blue-400 hover:text-blue-300 hover:underline"
                      onClick={() => onTickerClick && onTickerClick(visibleTickers[0])}
                    >
                      {visibleTickers[0]}
                    </span>
                  </th>
                  {filteredYears.map(year => (
                    <th
                      key={year}
                      className="text-right py-3 px-3 font-semibold"
                      style={{ backgroundColor: '#1f2937', color: '#9ca3af', minWidth: '90px' }}
                    >
                      {year}
                    </th>
                  ))}
                </tr>
              ) : (
                /* Multiple tickers: dual header — tickers spanning, years as sub-columns */
                <>
                  <tr className="border-b border-gray-700" style={{ backgroundColor: '#1f2937' }}>
                    <th
                      rowSpan={2}
                      className="text-left py-3 px-4 sticky left-0 z-10 font-semibold"
                      style={{ backgroundColor: '#1f2937', color: '#ffffff', minWidth: '160px' }}
                    >
                      Metric
                    </th>
                    {visibleTickers.map((ticker, tickerIdx) => {
                      const globalTickerIdx = data.tickers.indexOf(ticker);
                      const color = TICKER_COLORS[globalTickerIdx % TICKER_COLORS.length];
                      return (
                        <th
                          key={ticker}
                          colSpan={filteredYears.length}
                          className={`text-center py-2 px-2 font-semibold cursor-pointer hover:opacity-80 ${tickerIdx > 0 ? 'border-l-2 border-gray-500' : 'border-l border-gray-600'}`}
                          style={{ backgroundColor: '#1f2937', color }}
                          onClick={() => onTickerClick && onTickerClick(ticker)}
                        >
                          {ticker}
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-gray-700" style={{ backgroundColor: '#111827' }}>
                    {visibleTickers.map((ticker, tickerIdx) =>
                      filteredYears.map((year, yearIdx) => (
                        <th
                          key={`${ticker}-${year}`}
                          className={`text-right py-2 px-3 text-xs font-normal ${yearIdx === 0 ? (tickerIdx > 0 ? 'border-l-2 border-gray-500' : 'border-l border-gray-600') : ''}`}
                          style={{ backgroundColor: '#111827', color: '#9ca3af', minWidth: '85px' }}
                        >
                          {year}
                        </th>
                      ))
                    )}
                  </tr>
                </>
              )}
            </thead>

            <tbody>
              {orderedSelectedMetrics.map((metric, metricIdx) => (
                <React.Fragment key={metric.key}>
                  {/* Metric data row */}
                  <tr
                    className={`border-b border-gray-800 hover:bg-[rgba(33,138,255,0.15)] transition-colors ${metricIdx % 2 === 0 ? 'bg-gray-800/30' : ''}`}
                  >
                    <td
                      className="py-2 px-4 font-medium sticky left-0 cursor-help"
                      style={{
                        backgroundColor: metricIdx % 2 === 0 ? '#1f2937' : '#111827',
                        color: '#60a5fa',
                        minWidth: '160px',
                      }}
                      data-tooltip-id="financials-metric-tooltip"
                      data-tooltip-content={METRIC_TOOLTIPS[metric.key]}
                    >
                      {metric.label}
                    </td>
                    {visibleTickers.length === 1 ? (
                      /* Single ticker: one value per year */
                      filteredYears.map(year => (
                        <td
                          key={`${metric.key}-${year}`}
                          className="py-2 px-3 text-right text-white"
                        >
                          {formatValue(data.ratios?.[metric.key]?.[visibleTickers[0]]?.[year], metric.key)}
                        </td>
                      ))
                    ) : (
                      /* Multiple tickers: years grouped under each ticker */
                      visibleTickers.map((ticker, tickerIdx) =>
                        filteredYears.map((year, yearIdx) => (
                          <td
                            key={`${metric.key}-${ticker}-${year}`}
                            className={`py-2 px-3 text-right text-white ${yearIdx === 0 ? (tickerIdx > 0 ? 'border-l-2 border-gray-500' : 'border-l border-gray-700') : ''}`}
                          >
                            {formatValue(data.ratios?.[metric.key]?.[ticker]?.[year], metric.key)}
                          </td>
                        ))
                      )
                    )}
                  </tr>

                  {/* Growth row in statement view */}
                  {showGrowthRows && (
                    <tr
                      className={`border-b border-gray-700 ${metricIdx % 2 === 0 ? 'bg-gray-800/20' : 'bg-gray-900/30'}`}
                    >
                      <td
                        className="py-1 px-4 text-xs text-gray-400 italic sticky left-0"
                        style={{ backgroundColor: metricIdx % 2 === 0 ? '#1a2332' : '#0f1419' }}
                      >
                        YoY Growth
                      </td>
                      {visibleTickers.length === 1 ? (
                        /* Single ticker growth */
                        filteredYears.map((year, yearIdx) => {
                          const currentVal = data.ratios?.[metric.key]?.[visibleTickers[0]]?.[year];
                          const prevYear = yearIdx > 0 ? filteredYears[yearIdx - 1] : null;
                          const prevVal = prevYear ? data.ratios?.[metric.key]?.[visibleTickers[0]]?.[prevYear] : null;
                          const growth = yearIdx === 0 ? null : calculateGrowth(currentVal, prevVal);
                          return (
                            <td
                              key={`${metric.key}-${year}-growth`}
                              className={`py-1 px-3 text-right text-xs ${
                                growth === null ? 'text-gray-500' :
                                growth >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {formatGrowth(growth)}
                            </td>
                          );
                        })
                      ) : (
                        /* Multi-ticker growth — years grouped under each ticker */
                        visibleTickers.map((ticker, tickerIdx) =>
                          filteredYears.map((year, yearIdx) => {
                            const currentVal = data.ratios?.[metric.key]?.[ticker]?.[year];
                            const prevYear = yearIdx > 0 ? filteredYears[yearIdx - 1] : null;
                            const prevVal = prevYear ? data.ratios?.[metric.key]?.[ticker]?.[prevYear] : null;
                            const growth = yearIdx === 0 ? null : calculateGrowth(currentVal, prevVal);
                            return (
                              <td
                                key={`${metric.key}-${ticker}-${year}-growth`}
                                className={`py-1 px-3 text-right text-xs ${yearIdx === 0 ? (tickerIdx > 0 ? 'border-l-2 border-gray-500' : 'border-l border-gray-700') : ''} ${
                                  growth === null ? 'text-gray-500' :
                                  growth >= 0 ? 'text-green-400' : 'text-red-400'
                                }`}
                              >
                                {formatGrowth(growth)}
                              </td>
                            );
                          })
                        )
                      )}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <ReactTooltip
            id="financials-metric-tooltip"
            place="bottom"
            delayShow={300}
            delayHide={100}
            style={{ maxWidth: '300px', zIndex: 50 }}
          />
        </div>
      )}

      {/* Footer Info */}
      <div className="flex justify-between items-center text-sm text-gray-400">
        <span>
          {viewMode === 'statement' ? visibleTickers.length : data.tickers.length} ticker{(viewMode === 'statement' ? visibleTickers.length : data.tickers.length) !== 1 ? 's' : ''} ×{' '}
          {orderedSelectedMetrics.length} metric{orderedSelectedMetrics.length !== 1 ? 's' : ''} ×{' '}
          {filteredYears.length} period{filteredYears.length !== 1 ? 's' : ''}
          {viewMode === 'statement' && ' — Statement View'}
        </span>
        <span>Data source: Polygon.io</span>
      </div>
    </div>
  );
}

export default ResearchFinancialsTable;
